<script lang="ts">
  import type { EditorState } from '../editor-state.svelte';
  import type { PanelSections } from '../panel-sections.svelte';
  import PanelRail from './PanelRail.svelte';
  import ResizeHandle from './ResizeHandle.svelte';
  import SectionHeading from './SectionHeading.svelte';
  import { RIGHT_PANEL_SECTIONS } from './right-panel-sections';

  const { editor, panel }: { editor: EditorState; panel: PanelSections } = $props();

  // Showing, hiding or resizing the column resizes the canvas, which mid-gesture would shift
  // the drawing under the pointer.
  const resizeLocked = $derived(editor.inProgress);
</script>

<aside class="right" aria-label="Parameters, script and comments">
  <!-- Every section stays mounted and hidden, so the code editor keeps its text, cursor and
       scroll. -->
  <div
    id="right-panel-sections"
    class="sections"
    hidden={panel.collapsed}
    style:width="{panel.width}px"
  >
    {#each RIGHT_PANEL_SECTIONS as section (section.id)}
      <section
        class="section"
        hidden={!panel.isShown(section.id)}
        aria-labelledby="right-section-{section.id}"
      >
        <SectionHeading {editor} {section} id="right-section-{section.id}" />
        <section.body {editor} />
      </section>
    {/each}
    <!-- Pinned under every section, since params and the script both run the drawing. -->
    <div class="status" class:failed={editor.scriptError !== null} role="status">
      {#if editor.scriptError}
        {editor.scriptError.line === null ? '' : `Line ${editor.scriptError.line}: `}{editor
          .scriptError.message}
      {:else}
        {editor.scene.entities.length + editor.scene.dimensions.length + editor.scene.views.length} objects
      {/if}
    </div>
  </div>
  {#if !panel.collapsed}
    <ResizeHandle
      {panel}
      edge="left"
      label="Resize right panel"
      controls="right-panel-sections"
      locked={resizeLocked}
    />
  {/if}
  <PanelRail
    {panel}
    sections={RIGHT_PANEL_SECTIONS}
    side="right"
    name="right panel"
    controls="right-panel-sections"
    locked={resizeLocked}
  />
</aside>

<style>
  .right {
    position: relative;
    display: flex;
    min-height: 0;
    border-left: 1px solid var(--color-border);
    background: var(--color-surface);
  }

  .sections {
    display: flex;
    flex: none;
    flex-direction: column;
    min-height: 0;
  }

  .sections[hidden],
  .section[hidden] {
    display: none;
  }

  /* A column, so a body's list or the code editor takes the height left under the heading. */
  .section {
    display: flex;
    flex: 1;
    flex-direction: column;
    min-height: 0;
    overflow-y: auto;
    padding: var(--space-2) var(--space-3) var(--space-3);
  }

  /* One line tall at all times, so an error never resizes the sections above it. */
  .status {
    flex: none;
    height: 1.75rem;
    padding: 0 var(--space-3);
    overflow: hidden;
    border-top: 1px solid var(--color-border);
    color: var(--color-text-muted);
    font-size: 0.8125rem;
    line-height: 1.75rem;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .status.failed {
    color: var(--color-danger);
  }
</style>
